import { captureException, captureMessage } from "@sentry/react";
import type { LocalParticipant } from "livekit-client";
import { LocalVideoTrack, Track } from "livekit-client";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { uiStore } from "@/lib/stores/ui-store";
import type { ScreenSource } from "@/types/electron";
import { useElectronScreenShare } from "./use-electron-screen-share";

function handleScreenShareError(error: unknown, sentryContext: string) {
	const errorMessage = error instanceof Error ? error.message : String(error);
	const isExpectedError =
		errorMessage.includes("NotAllowedError") ||
		errorMessage.includes("Permission denied") ||
		errorMessage.includes("AbortError") ||
		errorMessage.includes("DeviceUnsupportedError") ||
		errorMessage.includes("getDisplayMedia not supported");

	if (
		errorMessage.includes("NotAllowedError") ||
		errorMessage.includes("Permission denied")
	) {
		toast.error(
			"Screen share permission denied. Please allow screen sharing when prompted"
		);
	} else if (
		errorMessage.includes("DeviceUnsupportedError") ||
		errorMessage.includes("getDisplayMedia not supported")
	) {
		toast.warning(
			"Screen sharing is not supported on this device or browser."
		);
	} else if (!errorMessage.includes("AbortError")) {
		console.error("Failed to share screen", error);
		toast.error("Failed to share screen");
	}

	if (SENTRY_ENABLED) {
		if (isExpectedError) {
			captureMessage(`Screen share expected error: ${errorMessage}`, {
				level: "info",
				tags: { context: sentryContext },
			});
		} else {
			captureException(error, { tags: { context: sentryContext } });
		}
	}
}

export type UseScreenShareOptions = {
	localParticipant: LocalParticipant | undefined;
	sentryContext?: string;
	onScreenShareChange?: (enabled: boolean) => void;
};

export type UseScreenShareReturn = {
	screenShareEnabled: boolean;
	toggleScreenShare: () => Promise<void>;
	handleElectronScreenShare: (source: ScreenSource) => Promise<void>;
	screenPickerOpen: boolean;
	setScreenPickerOpen: (open: boolean) => void;
	screenSources: ScreenSource[];
	isElectron: boolean;
	sharedSourceName: string | null;
};

export function useScreenShare({
	localParticipant,
	sentryContext = "screen_share",
	onScreenShareChange,
}: UseScreenShareOptions): UseScreenShareReturn {
	const { isElectron, getScreenSources, createScreenShareStream } =
		useElectronScreenShare();
	const [screenSources, setScreenSources] = useState<ScreenSource[]>([]);
	const [screenPickerOpen, setScreenPickerOpen] = useState(false);
	const screenShareIndicator = useStore(
		uiStore,
		(state) => state.screenShareIndicator
	);
	const setScreenShareIndicator = useStore(
		uiStore,
		(state) => state.setScreenShareIndicator
	);
	const screenShareTrackRef = useRef<LocalVideoTrack | null>(null);

	const screenShareEnabled =
		localParticipant?.isScreenShareEnabled &&
		localParticipant?.getTrackPublication(Track.Source.ScreenShare)?.track
			?.isMuted === false;

	const handleElectronScreenShare = useCallback(
		async (source: ScreenSource) => {
			if (!localParticipant) return;
			try {
				const stream = await createScreenShareStream(source.id);
				if (!stream) return;

				const videoTrack = stream.getVideoTracks()[0];
				if (!videoTrack) {
					toast.error("Failed to get video track from screen share");
					return;
				}

				const localTrack = new LocalVideoTrack(videoTrack, undefined, false);
				screenShareTrackRef.current = localTrack;

				await localParticipant.publishTrack(localTrack, {
					name: "screen",
					source: Track.Source.ScreenShare,
				});
				setScreenShareIndicator({
					sourceName: source.name,
					isVisible: true,
					position: { x: -1, y: -1 },
				});
				onScreenShareChange?.(true);
			} catch (error) {
				if (SENTRY_ENABLED)
					captureException(error, {
						tags: { context: `${sentryContext}_electron` },
					});
				console.error("Failed to share screen", error);
				toast.error("Failed to share screen");
			}
		},
		[
			localParticipant,
			createScreenShareStream,
			sentryContext,
			onScreenShareChange,
		]
	);

	const toggleScreenShare = useCallback(async () => {
		if (!localParticipant) return;

		if (screenShareEnabled) {
			if (isElectron && screenShareTrackRef.current) {
				await localParticipant.unpublishTrack(screenShareTrackRef.current);
				screenShareTrackRef.current.stop();
				screenShareTrackRef.current = null;
			} else {
				await localParticipant.setScreenShareEnabled(false);
			}
			setScreenShareIndicator({ sourceName: null });
			onScreenShareChange?.(false);
			return;
		}

		if (isElectron) {
			const sources = await getScreenSources();
			if (sources.length === 0) return;
			setScreenSources(sources);
			setScreenPickerOpen(true);
			return;
		}

		if (!navigator.mediaDevices?.getDisplayMedia) {
			toast.warning(
				"Screen sharing is not supported on this device or browser."
			);
			return;
		}

		try {
			await localParticipant.setScreenShareEnabled(true, {
				audio: true,
			});
			onScreenShareChange?.(true);
		} catch (error) {
			handleScreenShareError(error, sentryContext);
		}
	}, [
		localParticipant,
		screenShareEnabled,
		isElectron,
		getScreenSources,
		sentryContext,
		onScreenShareChange,
	]);

	return {
		screenShareEnabled: !!screenShareEnabled,
		toggleScreenShare,
		handleElectronScreenShare,
		screenPickerOpen,
		setScreenPickerOpen,
		screenSources,
		isElectron,
		sharedSourceName: screenShareIndicator.sourceName,
	};
}
